import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Plus, Search, Edit, Trash2, Eye, 
  Phone, Calendar, IndianRupee, MoreVertical, UserCheck, UserX,
  MessageSquare, Copy, Check
} from 'lucide-react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { apiClient } from '../../App';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const MembersPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderMessage, setReminderMessage] = useState('');
  const [reminderStats, setReminderStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loadingReminder, setLoadingReminder] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    password: '',
    initial_capital: 5000,
    total_capital: 0,
    pending_loan: 0
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await apiClient.get('/members');
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to fetch members');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedMember) {
        await apiClient.put(`/members/${selectedMember.id}`, {
          name: formData.name,
          phone: formData.phone,
          initial_capital: formData.initial_capital,
          total_capital: formData.total_capital,
          pending_loan: formData.pending_loan
        });
        toast.success('Member updated successfully');
      } else {
        await apiClient.post('/members', formData);
        toast.success('Member added successfully');
      }
      setShowAddModal(false);
      setFormData({ name: '', phone: '', password: '', initial_capital: 5000, total_capital: 0, pending_loan: 0 });
      setIsEditing(false);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save member');
    }
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setFormData({
      name: member.name,
      phone: member.phone,
      password: '',
      initial_capital: member.initial_capital,
      total_capital: member.total_capital || 0,
      pending_loan: member.pending_loan || 0
    });
    setIsEditing(true);
    setShowAddModal(true);
  };

  const handleViewMember = (member) => {
    setSelectedMember(member);
    setShowViewModal(true);
  };

  const handleDeleteMember = async () => {
    try {
      await apiClient.delete(`/members/${selectedMember.id}`);
      toast.success('Member deleted successfully');
      setShowDeleteDialog(false);
      fetchMembers();
    } catch (error) {
      toast.error('Failed to delete member');
    }
  };

  const handleToggleStatus = async (member) => {
    try {
      await apiClient.put(`/members/${member.id}`, {
        is_active: !member.is_active
      });
      toast.success(`Member ${member.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchMembers();
    } catch (error) {
      toast.error('Failed to update member status');
    }
  };

  const handleRemindAll = async () => {
    setLoadingReminder(true);
    try {
      const response = await apiClient.get('/reminders/generate-message');
      setReminderMessage(response.data.message);
      setReminderStats(response.data.stats);
      setShowReminderModal(true);
    } catch (error) {
      toast.error('Failed to generate reminder message');
    } finally {
      setLoadingReminder(false);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(reminderMessage);
      setCopied(true);
      toast.success('Message copied to clipboard! Paste it in WhatsApp.');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const filteredMembers = members.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.phone.includes(searchTerm)
  );

  return (
    <AdminLayout title="Members" subtitle="Manage group members">
      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Member' : 'Add New Member'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddMember} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter full name"
                data-testid="member-name-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Enter phone number"
                data-testid="member-phone-input"
                required
              />
            </div>
            {!isEditing && (
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter password"
                  data-testid="member-password-input"
                  required
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="capital">Initial Capital (₹)</Label>
              <Input
                id="capital"
                type="number"
                value={formData.initial_capital}
                onChange={(e) => setFormData({ ...formData, initial_capital: parseFloat(e.target.value) })}
                placeholder="Enter initial capital"
                data-testid="member-capital-input"
                required
              />
            </div>
            {isEditing && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="total_capital">Total Capital (₹)</Label>
                  <Input
                    id="total_capital"
                    type="number"
                    value={formData.total_capital}
                    onChange={(e) => setFormData({ ...formData, total_capital: parseFloat(e.target.value) })}
                    placeholder="Enter total capital"
                    data-testid="member-total-capital-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pending_loan">Pending Loan (₹)</Label>
                  <Input
                    id="pending_loan"
                    type="number"
                    value={formData.pending_loan}
                    onChange={(e) => setFormData({ ...formData, pending_loan: parseFloat(e.target.value) })}
                    placeholder="Enter pending loan amount"
                    data-testid="member-pending-loan-input"
                  />
                </div>
              </>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowAddModal(false);
                setIsEditing(false);
                setFormData({ name: '', phone: '', password: '', initial_capital: 5000, total_capital: 0, pending_loan: 0 });
              }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#0A2540] hover:bg-[#163A5F]" data-testid="member-submit-btn">
                {isEditing ? 'Update' : 'Add'} Member
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Member Details</DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#0A2540] flex items-center justify-center text-white text-2xl font-bold">
                  {selectedMember.name.charAt(0)}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#0A2540]">{selectedMember.name}</h3>
                  <p className="text-[#425466]">{selectedMember.phone}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div className="p-4 rounded-xl bg-[#F4F7FB]">
                  <p className="text-sm text-[#425466]">Total Capital</p>
                  <p className="text-lg font-bold text-[#0A2540]">{formatCurrency(selectedMember.total_capital)}</p>
                </div>
                <div className="p-4 rounded-xl bg-[#F4F7FB]">
                  <p className="text-sm text-[#425466]">Contributions</p>
                  <p className="text-lg font-bold text-[#00C853]">{formatCurrency(selectedMember.total_contributions)}</p>
                </div>
                <div className="p-4 rounded-xl bg-[#F4F7FB]">
                  <p className="text-sm text-[#425466]">Pending Loan</p>
                  <p className="text-lg font-bold text-[#2962FF]">{formatCurrency(selectedMember.pending_loan)}</p>
                </div>
                <div className="p-4 rounded-xl bg-[#F4F7FB]">
                  <p className="text-sm text-[#425466]">Penalties</p>
                  <p className="text-lg font-bold text-red-500">{formatCurrency(selectedMember.total_penalties)}</p>
                </div>
              </div>
              <div className="pt-4 border-t">
                <p className="text-sm text-[#425466]">
                  <span className="font-medium">Joining Date:</span> {new Date(selectedMember.joining_date).toLocaleDateString('en-IN')}
                </p>
                <p className="text-sm text-[#425466] mt-1">
                  <span className="font-medium">Status:</span>{' '}
                  <span className={selectedMember.is_active ? 'text-green-600' : 'text-red-600'}>
                    {selectedMember.is_active ? 'Active' : 'Inactive'}
                  </span>
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedMember?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteMember} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reminder Message Modal */}
      <Dialog open={showReminderModal} onOpenChange={setShowReminderModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#00C853]" />
              WhatsApp Reminder Message
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {reminderStats && (
              <div className="flex gap-4 text-sm">
                <div className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full">
                  {reminderStats.pending_contributions} Pending Contributions
                </div>
                <div className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                  {reminderStats.pending_emis} Pending EMIs
                </div>
                {reminderStats.overdue_emis > 0 && (
                  <div className="px-3 py-1 bg-red-100 text-red-700 rounded-full">
                    {reminderStats.overdue_emis} Overdue
                  </div>
                )}
              </div>
            )}
            <div className="bg-[#F4F7FB] rounded-xl p-4 max-h-[400px] overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm text-[#0A2540] font-sans leading-relaxed">
                {reminderMessage}
              </pre>
            </div>
            <p className="text-sm text-[#425466]">
              Click the button below to copy this message, then paste it in your WhatsApp group.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReminderModal(false)}>
              Close
            </Button>
            <Button 
              onClick={handleCopyToClipboard}
              className="bg-[#00C853] hover:bg-[#00A843] text-white"
              data-testid="copy-reminder-btn"
            >
              {copied ? (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5 mr-2" />
                  Copy to Clipboard
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#425466]" />
          <Input
            placeholder="Search members..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-members-input"
          />
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleRemindAll}
            disabled={loadingReminder}
            variant="outline"
            className="border-[#00C853] text-[#00C853] hover:bg-[#00C853]/10"
            data-testid="remind-all-btn"
          >
            <MessageSquare className="w-5 h-5 mr-2" />
            {loadingReminder ? 'Generating...' : 'Remind All'}
          </Button>
          <Button 
            onClick={() => {
              setIsEditing(false);
              setFormData({ name: '', phone: '', password: '', initial_capital: 5000 });
              setShowAddModal(true);
            }}
            className="bg-[#0A2540] hover:bg-[#163A5F]"
            data-testid="add-member-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Member
          </Button>
        </div>
      </div>

      {/* Members Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="dashboard-card overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#F4F7FB] border-b border-gray-100">
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Member</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Phone</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Total Capital</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Pending Loan</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-[#425466] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4"><div className="skeleton h-10 w-40 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-28 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-24 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-24 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-16 rounded-full"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-8 w-8 rounded"></div></td>
                  </tr>
                ))
              ) : filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-[#425466]">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No members found</p>
                  </td>
                </tr>
              ) : (
                filteredMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#0A2540] flex items-center justify-center text-white font-medium">
                          {member.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-[#0A2540]">{member.name}</p>
                          <p className="text-xs text-[#425466]">Since {new Date(member.joining_date).toLocaleDateString('en-IN')}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-[#425466]">
                        <Phone className="w-4 h-4" />
                        {member.phone}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-[#0A2540]">{formatCurrency(member.total_capital)}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className={`font-semibold ${member.pending_loan > 0 ? 'text-[#2962FF]' : 'text-[#425466]'}`}>
                        {formatCurrency(member.pending_loan)}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        member.is_active ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                      }`}>
                        {member.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`member-actions-${member.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewMember(member)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditMember(member)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleStatus(member)}>
                            {member.is_active ? (
                              <>
                                <UserX className="w-4 h-4 mr-2" />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <UserCheck className="w-4 h-4 mr-2" />
                                Activate
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => {
                              setSelectedMember(member);
                              setShowDeleteDialog(true);
                            }}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Summary */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="dashboard-card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#0A2540]/10 flex items-center justify-center">
            <Users className="w-6 h-6 text-[#0A2540]" />
          </div>
          <div>
            <p className="text-sm text-[#425466]">Total Members</p>
            <p className="text-xl font-bold text-[#0A2540]">{members.length}</p>
          </div>
        </div>
        <div className="dashboard-card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
            <UserCheck className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-[#425466]">Active Members</p>
            <p className="text-xl font-bold text-green-600">{members.filter(m => m.is_active).length}</p>
          </div>
        </div>
        <div className="dashboard-card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#00C853]/10 flex items-center justify-center">
            <IndianRupee className="w-6 h-6 text-[#00C853]" />
          </div>
          <div>
            <p className="text-sm text-[#425466]">Total Capital</p>
            <p className="text-xl font-bold text-[#00C853]">
              {formatCurrency(members.reduce((sum, m) => sum + (m.total_capital || 0), 0))}
            </p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default MembersPage;
